import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import { useTranslation } from '../../src/i18n';
import { colors, fontSize } from '../../constants/styleConstants';
import StubPageSocialLinks from '../StubPageSocialLinks';
import NextLink from '../NextLink';
import RectangleButton from '../RectangleButton';
import { TP } from '../../constants/consts';
import ImageTag from '../ImageTag';
import ActionsBlock from '../ActionsBlock';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    padding: '30px 20px 20px 20px',
    fontFamily: 'Roboto',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  nameSection: {
    paddingTop: 16,
    paddingBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  name: {
    fontSize: fontSize.h2,
    fontWeight: 'bold',
    color: colors.grey3,
    fontFamily: 'Roboto',
    [theme.breakpoints.down('xs')]: {
      fontSize: fontSize.h6,
    },
  },
  region: {
    fontSize: fontSize.h6,
    color: colors.grye3,
    [theme.breakpoints.down('xs')]: {
      fontStyle: 'italic',
      fontSize: fontSize.regularTitle,
    },
  },
  logo: {
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    [theme.breakpoints.down('xs')]: {
      width: '85px !important',
      height: '85px !important',
    },
  },
  circle: {
    width: '140px',
    height: '140px',
    '& img': {
      borderRadius: '50%',
      border: `1px solid ${colors.border.grey1}`,
    },
  },
  square: {
    width: '152px',
    height: '154px',
    '& img': {
      borderRadius: '8px',
      border: `1px solid #8b634a`,
    },
  },
  content: {
    fontSize: fontSize.medium,
    lineHeight: 1.64,
    color: colors.cancelButton,
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    '& p': {
      overflow: 'hidden',
      margin: 0,
    },
    '& span': {
      color: 'rgba(45, 46, 49, 0.5)',
    },
  },
  moreLink: {
    color: colors.blue,
    fontSize: fontSize.medium,
    textTransform: 'lowercase',
    cursor: 'pointer',
    width: 'fit-content',
    paddingLeft: props => (props.isBio ? 15 : ''),
  },

  rightIcons: {
    marginLeft: 'auto',
    display: 'flex',
    marginBottom: '9px',
  },
  shareButton: {
    marginBottom: 'auto',
    marginRight: 5,
  },
  editButton: {
    borderRadius: '8px',
    boxShadow: '0 2px 2px 0 rgba(9, 66, 135, 0.25)',
    backgroundColor: '#0067c5',
    minWidth: '67px',
    minHeight: '32px',
    padding: '4px 0',
    '&:hover': {
      backgroundColor: '#0067c5',
    },
  },
  dates: {
    color: colors.cancelButton,
    fontSize: fontSize.medium,
    lineHeight: 1.64,
    '& span': {
      color: 'rgba(45, 46, 49, 0.5)',
    },
  },
}));

const StubPageInfoCard = ({ className, data, contacts, switchTab, canEdit, editLinkProps, datesTitle }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <div className={cn(classes.root, className)}>
      {canEdit ? (
        <div className={classes.rightIcons}>
          <NextLink href={editLinkProps.href} as={editLinkProps.as}>
            <RectangleButton btnText={t(`${TP}.m_EDIT`)} iconType="edit" />
          </NextLink>
        </div>
      ) : (
        <ActionsBlock />
      )}
      {data?.logo && (
        <div className={cn(classes.logo, data.logoType === 1 ? classes.circle : classes.square)}>
          <ImageTag src={data.logo} alt="name" />
        </div>
      )}
      <div className={classes.nameSection}>
        <div className={classes.name}>{data.name}</div>
        <div className={classes.region}>{data.region}</div>
        <div className={classes.region}>{data?.info}</div>
        {data?.date && (
          <div className={classes.dates}>
            <span>{datesTitle}</span> {data.date}
          </div>
        )}
      </div>
      <StubPageSocialLinks contacts={contacts} showContact={contacts?.length > 0} showContactsTab={() => switchTab()} />
    </div>
  );
};

StubPageInfoCard.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object,
  contacts: PropTypes.array,
  switchTab: PropTypes.func,
  editLinkProps: PropTypes.oneOfType([PropTypes.shape({ ...NextLink.propTypes }), PropTypes.func]),
  datesTitle: PropTypes.string,
};

export default StubPageInfoCard;
